import { useLocation } from 'react-router-dom';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useLeadsUnread } from '@/features/leads/hooks';
import { cn } from '@/lib/utils';

import { InstallAppButton } from './InstallAppButton';
import { EntradaDelMenu } from './navegacion/EntradaDelMenu';
import { EspacioDelMenu } from './navegacion/EspacioDelMenu';
import { resolverEntradaActiva } from './navegacion/entrada-activa';
import { useEspaciosAbiertos } from './navegacion/useEspaciosAbiertos';
import { useRegistroVisible } from './navegacion/useRegistroVisible';

export function AppSidebar() {
  const arbol = useRegistroVisible();
  // Respuestas de leads sin leer → contador de la entrada Leads (o de su espacio
  // si está plegado). El realtime de leads (AppLayout) invalida la query.
  const { data: leadsUnread = [] } = useLeadsUnread();
  const contadores = { 'leads-sin-leer': leadsUnread.length };
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === 'collapsed' && !isMobile;
  const location = useLocation();
  const activa = resolverEntradaActiva(arbol, location);
  const { estaAbierto, alternar } = useEspaciosAbiertos(activa?.espacio?.id ?? null);
  // Elegir una entrada cierra el panel del teléfono; desplegar un espacio no.
  const cerrarPanelDelTelefono = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon">
      {/* Brand row — solo decoración. El toggle vive en el Topbar.
          Minimal: logo limpio (sin caja ni reborde, glow del logo intacto) +
          nombre en Montserrat. Collapsed (48px): logo centrado.
      */}
      <SidebarHeader
        className={cn(
          'border-b py-0',
          isCollapsed
            ? 'flex h-14 items-center justify-center px-0'
            : 'flex h-14 flex-row items-center gap-2.5 px-3',
        )}
      >
        <div className="brand-mark pointer-events-none">
          <img
            src="/logo.webp"
            alt=""
            draggable={false}
            className="block h-full w-full object-contain"
          />
        </div>

        {!isCollapsed && (
          <span className="font-display text-foreground truncate text-title font-semibold">
            DenDev Core
          </span>
        )}
      </SidebarHeader>

      <SidebarContent>
        {arbol.map((seccion) => (
          <SidebarGroup key={seccion.id}>
            {seccion.titulo && (
              <SidebarGroupLabel className="section-label">{seccion.titulo}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {seccion.elementos.map((el) =>
                  el.tipo === 'entrada' ? (
                    <EntradaDelMenu
                      key={el.id}
                      entrada={el}
                      activa={el.id === activa?.entrada.id}
                      contadores={contadores}
                      onElegir={cerrarPanelDelTelefono}
                    />
                  ) : (
                    <EspacioDelMenu
                      key={el.id}
                      espacio={el}
                      abierto={estaAbierto(el.id)}
                      plegadoAIconos={isCollapsed}
                      entradaActivaId={activa?.entrada.id ?? null}
                      contadores={contadores}
                      onAlternar={() => alternar(el.id)}
                      onElegir={cerrarPanelDelTelefono}
                    />
                  ),
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Footer · botón de instalación PWA. El componente renderiza su propio
          SidebarFooter solo cuando hay algo para mostrar (ver useInstallPrompt):
          se auto-oculta si ya está instalada o el browser no soporta instalación. */}
      <InstallAppButton />

      <SidebarRail />
    </Sidebar>
  );
}
